import {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  serializeFormData,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { CommonErrorCodesEnumType } from '~/common/types';
import { ConnectQueryKeys } from '~/connect/api';
import {
  IOdrOperation,
  IOdrOperationPayload,
  IOdrOperationUpdatePayload,
} from '~/connect/types';

type Options = {
  create?: UseMutationOptions<
    ISuccessApiResponse<IOdrOperation>,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >,
    IOdrOperationPayload
  >;
  getOneById?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IOdrOperation>, IErrorApiResponse>
  > & { args?: { id: string } };
  update?: UseMutationOptions<
    ISuccessApiResponse<IOdrOperation>,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >,
    { id: string; body: IOdrOperationUpdatePayload }
  >;
  remove?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<undefined, CommonErrorCodesEnumType>,
    string
  >;
  toggleCustomerStatus?: UseMutationOptions<
    ISuccessApiResponse<IOdrOperation>,
    IErrorApiResponse<undefined, CommonErrorCodesEnumType>,
    { id: string; customerId: string }
  >;
};

export const useManageOdrOperations = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const endpoint = `/tenants/api/wtb/tenants/${tenantCode}/odr-operations`;

  const create = useMutation({
    mutationFn: ({ file, ...payload }: IOdrOperationPayload) => {
      const body = new FormData();
      body.append('file', file, file.name);
      // serializeFormData renders the nested payload as the bracket keys PHP expands back into arrays
      new URLSearchParams(serializeFormData(payload)).forEach((value, key) => {
        body.append(key, value);
      });

      return callCoreApi<ISuccessApiResponse<IOdrOperation>>(endpoint, {
        method: 'POST',
        body,
        asJson: false,
      });
    },
    keysToInvalidateOnSuccess: () => [
      ConnectQueryKeys.odrOperations.base(tenantCode),
    ],
    ...options.create,
  });

  const getOneById = useQuery({
    queryKey: ConnectQueryKeys.odrOperations.detail(
      tenantCode,
      options.getOneById?.args?.id,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IOdrOperation>>(
        `${endpoint}/${options.getOneById!.args!.id}`,
      ),
    enabled: !!options.getOneById?.args?.id,
    ...options.getOneById,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      body: { file, ...payload },
    }: {
      id: string;
      body: IOdrOperationUpdatePayload;
    }) => {
      const body = new FormData();
      if (file) {
        body.append('file', file, file.name);
      }
      // serializeFormData renders the nested payload as the bracket keys PHP expands back into arrays
      new URLSearchParams(serializeFormData(payload)).forEach((value, key) => {
        body.append(key, value);
      });

      return callCoreApi<ISuccessApiResponse<IOdrOperation>>(
        `${endpoint}/${id}`,
        {
          method: 'POST',
          body,
          asJson: false,
        },
      );
    },
    keysToInvalidateOnSuccess: ({ id }) => [
      ConnectQueryKeys.odrOperations.detail(tenantCode, id),
      ConnectQueryKeys.odrOperations.base(tenantCode),
    ],
    ...options.update,
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      callCoreApi<ISuccessApiResponse>(`${endpoint}/${id}`, {
        method: 'DELETE',
      }),
    keysToInvalidateOnSuccess: () => [
      ConnectQueryKeys.odrOperations.base(tenantCode),
    ],
    ...options.remove,
  });

  const toggleCustomerStatus = useMutation({
    mutationFn: ({ id, customerId }: { id: string; customerId: string }) =>
      callCoreApi<ISuccessApiResponse<IOdrOperation>>(
        `${endpoint}/${id}/customers/${customerId}/toggle-status`,
        { method: 'PATCH' },
      ),
    keysToInvalidateOnSuccess: () => [
      ConnectQueryKeys.odrOperations.base(tenantCode),
    ],
    ...options.toggleCustomerStatus,
  });

  return restrictApi(options, {
    create,
    getOneById,
    update,
    remove,
    toggleCustomerStatus,
  });
};
